import { appConfig } from "@shared/utils/appConfig";
import { ResponseError } from "./generated";

const UNEXPECTED_ERROR =
  "Unexpected error, please try again or contact support if the issue persists.";
const UNKNOWN_ERROR = "Unknown Error. Please, contact the support.";
const NETWORK_ERROR = "Error fetching the data. Connection refused.";

type ErrorType = "RESPONSE_ERROR" | "UNKNOWN_ERROR" | "NETWORK_ERROR";

export const apiResponseErrorHandler = async (
  error: any
): Promise<ApiError> => {
  if (!error) {
    return { errors: [], status: 500, type: "UNKNOWN_ERROR" };
  }

  if (error.name === "FetchError")
    return { errors: [NETWORK_ERROR], status: 500, type: "NETWORK_ERROR" };

  if (error.name !== "ResponseError")
    return { errors: [UNKNOWN_ERROR], status: 500, type: "UNKNOWN_ERROR" };

  const err = error as ResponseError;
  let serverError: ApiResponseError;

  try {
    serverError = (await err.response.json()) as ApiResponseError;
  } catch {
    serverError = err.response as unknown as ApiResponseError;
  }

  try {
    if (err.response.status >= 500 && err.response.status < 600) {
      const errors = serverError.serverMessage
        ? [serverError.serverMessage]
        : [UNEXPECTED_ERROR];
      return {
        errors,
        status: err.response.status,
        type: "RESPONSE_ERROR",
      };
    }

    if (err.response.status >= 400 && err.response.status < 500) {
      if (appConfig.NODE_ENV === "development") {
        console.error("[API ERROR HANDLER]:", { serverError });
      }

      let errors: string[] = [];

      if (serverError?.customMessage) {
        errors = Array.isArray(serverError.customMessage)
          ? serverError.customMessage
          : [serverError.customMessage];
      } else if (serverError?.serverMessage) {
        errors = [serverError.serverMessage];
      } else if (serverError?.message) {
        errors = [serverError.message];
      } else {
        errors = [UNEXPECTED_ERROR];
      }

      return {
        errors,
        status: err.response.status,
        type: "RESPONSE_ERROR",
      };
    }

    return {
      errors: [UNEXPECTED_ERROR],
      status: err.response.status,
      type: "UNKNOWN_ERROR",
    };
  } catch {
    return { errors: [UNEXPECTED_ERROR], status: 500, type: "UNKNOWN_ERROR" };
  }
};

export interface ApiError {
  errors: string[];
  status: number;
  type: ErrorType;
}

interface ApiResponseError {
  statusCode: number;
  serverMessage: string;
  message?: string;
  customMessage?: string[];
  timestamp: string;
  path: string;
}
